import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { create, supported } from '@github/webauthn-json';
import { postJson, postTurboStream } from '../../lib/passkeys';

export default class extends Controller {
  static targets = ['nickname', 'password', 'submit', 'error', 'openButton'];
  static values = {
    challengeUrl: String,
    createUrl: String,
    unsupportedMessage: String,
    cancelledMessage: String,
    duplicateMessage: String,
    genericMessage: String,
  };

  connect() {
    if (!supported()) {
      this.openButtonTarget.disabled = true;
      this.openButtonTarget.title = this.unsupportedMessageValue;
    }
  }

  async register(event) {
    event.preventDefault();

    this.#hideError();
    this.submitTarget.disabled = true;

    try {
      const options = await this.#requestChallenge();

      if (options) {
        await this.#persist(await create({ publicKey: options }));
      }
    } catch (error) {
      this.#showError(this.#messageFor(error));
    } finally {
      this.submitTarget.disabled = false;
    }
  }

  async #requestChallenge() {
    const response = await postJson(this.challengeUrlValue, {
      users_passkey: { current_password: this.passwordTarget.value },
    });
    const body = await response.json();

    if (!response.ok) {
      this.#showError(body.error || this.genericMessageValue);
      return null;
    }

    return body;
  }

  async #persist(credential) {
    const response = await postTurboStream(this.createUrlValue, {
      users_passkey: {
        nickname: this.nicknameTarget.value,
        credential: credential,
      },
    });

    if (!response.ok) {
      this.#showError(this.genericMessageValue);
      return;
    }

    Turbo.renderStreamMessage(await response.text());
    this.#reset();
  }

  #messageFor(error) {
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
      return this.cancelledMessageValue;
    }

    if (error.name === 'InvalidStateError') {
      return this.duplicateMessageValue;
    }

    return this.genericMessageValue;
  }

  #reset() {
    this.nicknameTarget.value = '';
    this.passwordTarget.value = '';
    this.#hideError();
    this.element.querySelector('[data-bs-dismiss="modal"]')?.click();
  }

  #showError(message) {
    // Reveal before writing: a mutation inside display:none is never announced.
    this.errorTarget.classList.remove('d-none');
    this.errorTarget.textContent = message;
  }

  #hideError() {
    this.errorTarget.textContent = '';
    this.errorTarget.classList.add('d-none');
  }
}
